// src/pages/championships/ChampionshipTeamsTab.jsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import championshipService from '../../services/championshipService';
import teamService from '../../services/teamService';
import useAuth from '../../hooks/useAuth';
import './ChampionshipTeamsTab.css';

// ✅ Importação do fundo
import backgroundImage from '../../assets/background.png';

// ✅ IMPORTAR O COMPONENTE PARA EXIBIR O PIX
import PixDisplay from '../../components/PixDisplay'; 

// Função auxiliar para montar URL da logo do time
const getTeamLogoUrl = (badge) => {
  if (!badge) return '/default-team-logo.png';
  if (badge.startsWith('http')) return badge;
  const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace('/api', '');
  return `${baseUrl}/uploads/${badge}`;
};

function TeamLogo({ badge, name }) {
  const [hasError, setHasError] = useState(false);
  return (
    <img
      src={hasError ? '/default-team-logo.png' : getTeamLogoUrl(badge)}
      alt={`${name} logo`}
      onError={() => setHasError(true)}
      className="team-logo"
    />
  );
}

function ChampionshipTeamsTab() {
  const { id: championshipId } = useParams();
  const { user } = useAuth();

  const [enrolledTeams, setEnrolledTeams] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [championshipDetails, setChampionshipDetails] = useState(null);

  // ✅ Estados para controle de pagamento PIX
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });

  // ✅ Novo estado para controlar a visibilidade dos times disponíveis
  const [showAvailableTeams, setShowAvailableTeams] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [champDetailsRes, enrolledTeamsRes, allTeamsRes] = await Promise.all([
        championshipService.getById(championshipId),
        teamService.getTeamsByChampionship(championshipId),
        teamService.getAll()
      ]);

      const details = champDetailsRes.data || champDetailsRes;
      setChampionshipDetails(details);
      
      const enrolled = enrolledTeamsRes.data || enrolledTeamsRes;
      setEnrolledTeams(enrolled);
      
      const allTeams = allTeamsRes.data || allTeamsRes;
      if (user) {
        const enrolledIds = new Set(enrolled.map(t => t._id));
        const userOwnedTeams = allTeams.filter(t => t.creator === user._id && !enrolledIds.has(t._id));
        setAvailableTeams(userOwnedTeams);
      }

    } catch (err) {
      setError(err.message || 'Erro ao carregar dados dos times');
    } finally {
      setLoading(false);
    }
  }, [championshipId, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ✅ Função para toggle da seção de times disponíveis
  const toggleAvailableTeams = () => {
    setShowAvailableTeams(!showAvailableTeams);
  };

  // ✅ Função para inscrição de time
  const handleEnrollTeam = async (teamId, teamName) => {
    if (!window.confirm(`Você confirma a inscrição do time "${teamName}" neste campeonato?`)) return;

    setIsProcessingPayment(true);
    setMessage({ text: 'Processando inscrição...', type: 'info' });

    try {
      const response = await championshipService.addTeams(championshipId, [teamId]);
      
      if (response.data?.paymentRequired && response.data?.pixData) {
        setMessage({ text: '', type: '' });
        setPixData(response.data.pixData);
      } else {
        setMessage({ text: response.data?.message || 'Time inscrito com sucesso!', type: 'success' });
        await fetchData();
        setShowAvailableTeams(false);
      }

    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Ocorreu um erro.';
      setMessage({ text: errorMessage, type: 'error' });
    } finally {
      setIsProcessingPayment(false);
    }
  };
  
  const canPerformAdminActions = useMemo(() => {
    if (!user || !user.role || !championshipDetails) return false;
    if (user.role.toLowerCase() === 'admin') return true;
    if (String(user._id) === String(championshipDetails.creator?._id)) return true;
    if (championshipDetails.managers?.some(m => String(m._id) === String(user._id))) return true;
    return false;
  }, [user, championshipDetails]);
  
  const handleRemoveTeam = async (teamId, teamName) => {
    if (!window.confirm(`Tem certeza que deseja remover "${teamName}" do campeonato?`)) return;
    try {
      await teamService.removeTeamFromChampionship(championshipId, teamId);
      setMessage({ text: 'Time removido com sucesso!', type: 'success' });
      await fetchData();
    } catch (err) {
      setMessage({ text: err.response?.data?.error || 'Erro ao remover o time.', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="teams-section loading">
        <div className="loading-spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  if (error) {
    return <div className="teams-section"><p className="error">{error}</p></div>;
  }

  return (
    <>
      <div
        className="teams-section"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative'
        }}
      >
        <div className="overlay"></div>
        
        <div className="teams-content-wrapper">

          {message.text && (
            <div className={`form-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {availableTeams.length > 0 && (
            <div>
              <button 
                className={`toggle-teams-btn ${showAvailableTeams ? 'expanded' : ''}`}
                onClick={toggleAvailableTeams}
                type="button"
              >
                <span className="icon">▼</span>
                {showAvailableTeams ? 'Ocultar Meus Times' : `Mostrar Meus Times para Inscrever (${availableTeams.length})`}
              </button>
            </div>
          )}

          {availableTeams.length > 0 && (
            <div className={`available-teams-section ${showAvailableTeams ? 'expanded' : 'collapsed'}`}>
              {showAvailableTeams && (
                <>
                  <h3 className="section-title">Inscrever Meus Times</h3>
                  <div className="teams-grid">
                    {availableTeams.map(team => (
                      <div key={team._id} className="team-card available-card">
                        <div className="team-logo-container">
                          <TeamLogo badge={team.badge} name={team.name} />
                        </div>
                        <div className="team-info">
                          <h4>{team.name}</h4>
                          <p>{team.city}</p>
                        </div>
                        <button 
                          onClick={() => handleEnrollTeam(team._id, team.name)} 
                          className="button enroll-button"
                          disabled={isProcessingPayment}
                        >
                          {isProcessingPayment ? 'Aguarde...' : 'Inscrever neste Campeonato'}
                        </button>
                      </div>
                    ))}
                  </div>
                  <hr className="section-divider" />
                </>
              )}
            </div>
          )}

          {availableTeams.length === 0 && user && (
            <div style={{ marginBottom: '20px' }}>
              <p className="no-teams-message">
                Você não possui times disponíveis para inscrever ou todos os seus times já estão inscritos. 
                <Link to={`/teams/new?championshipId=${championshipId}`} state={{ championshipId }}>
                  Crie um novo time aqui.
                </Link>
              </p>
            </div>
          )}

          <div className="enrolled-teams-section">
            <div className="teams-header">
              <h2 className="section-title">Times Participantes ({enrolledTeams.length})</h2>
            </div>
            {enrolledTeams.length === 0 ? (
              <p className="no-teams-message">Nenhum time inscrito neste campeonato ainda.</p>
            ) : (
              <div className="teams-grid">
                {enrolledTeams.map((team) => (
                  <div key={team._id} className="team-card">
                    {/* 🟢 AQUI: Repassando o championshipId na Query String e no State */}
                    <Link 
                      to={`/teams/${team._id}?championshipId=${championshipId}`} 
                      state={{ championshipId }}
                      className="team-card-link-wrapper"
                    >
                      <div className="team-logo-container">
                        <TeamLogo badge={team.badge} name={team.name} />
                      </div>
                      <div className="team-info">
                        <h3>{team.name}</h3>
                        <p>{team.city}</p>
                      </div>
                    </Link>
                    {canPerformAdminActions && (
                      <div className="team-actions-footer">
                        <button
                          onClick={() => handleRemoveTeam(team._id, team.name)}
                          className="button delete-from-champ"
                        >
                          Remover do Campeonato
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {pixData && (
        <PixDisplay 
          pixData={{
            qrCodeImageUrl: pixData.qrCodeImage,
            qrCodeText: pixData.qrCodeText
          }} 
          onCancel={() => {
            setPixData(null);
            setMessage({ text: 'A inscrição do time ficará pendente até a confirmação do pagamento.', type: 'info' });
          }} 
        />
      )}
    </>
  );
}

export default ChampionshipTeamsTab;